import type { TeamCreationJobListItem } from "@bezoned/shared/types/api/team/team-creation-job-list.types.js";
import type { TeamListItem } from "@bezoned/shared/types/api/team/team-list.types.js";

export type TeamLobbyTeam = TeamListItem;

export type TeamLobbyRenderItem =
  | {
      key: string;
      kind: "synthetic-active";
      job: TeamCreationJobListItem;
      visualStatus: "pending" | "in_progress";
    }
  | {
      key: string;
      kind: "synthetic-failed";
      job: TeamCreationJobListItem;
    }
  | {
      key: string;
      kind: "create-button";
    }
  | {
      key: string;
      kind: "team";
      team: TeamLobbyTeam;
      highlighted: boolean;
    };

export type ActiveTeamLobbyRenderItem = Extract<
  TeamLobbyRenderItem,
  { kind: "synthetic-active" }
>;
export type FailedTeamLobbyRenderItem = Extract<
  TeamLobbyRenderItem,
  { kind: "synthetic-failed" }
>;
export type TeamLobbyTeamRenderItem = Extract<
  TeamLobbyRenderItem,
  { kind: "team" }
>;

export function isActiveCreateTeamStatus(
  status: TeamCreationJobListItem["status"]
) {
  return status === "pending" || status === "in_progress";
}

export function getCreateTeamActiveRowText(params: {
  requestedName: string;
  visualStatus: "pending" | "in_progress";
}) {
  if (params.visualStatus === "in_progress") {
    return `Office "${params.requestedName}" is being created.`;
  }

  return `Office "${params.requestedName}" is waiting to be created.`;
}

export function getCreateTeamFailedRowText(requestedName: string) {
  return `Office "${requestedName}" failed to be created.`;
}

export function shouldShowStarterSlotConsumedMessage(params: {
  hasAccessibleTeams: boolean;
  hasUnresolvedCreationJob: boolean;
  hasVisibleCreationResult: boolean;
  starterSlotConsumed: boolean;
}) {
  return (
    params.starterSlotConsumed &&
    !params.hasAccessibleTeams &&
    !params.hasUnresolvedCreationJob &&
    !params.hasVisibleCreationResult
  );
}

export function buildTeamLobbyRenderModel(params: {
  jobs: TeamCreationJobListItem[];
  teams: TeamLobbyTeam[];
  editMode: boolean;
}) {
  const teamsByMsTeamId = new Map(params.teams.map((team) => [team.id, team]));
  const highlightedTeamIds = new Set<string>();
  const unmatchedSuccessfulJobIds = new Set<string>();

  const activeItems = params.jobs.flatMap<ActiveTeamLobbyRenderItem>((job) => {
    if (job.status === "success") {
      if (job.acknowledgedAt !== null) {
        return [];
      }

      if (!(job.msTeamId && teamsByMsTeamId.has(job.msTeamId))) {
        unmatchedSuccessfulJobIds.add(job.jobId);
        return [];
      }

      highlightedTeamIds.add(job.msTeamId);
      return [];
    }

    if (!isActiveCreateTeamStatus(job.status)) {
      return [];
    }

    return [
      {
        job,
        key: `job:${job.jobId}`,
        kind: "synthetic-active",
        visualStatus: job.status,
      },
    ];
  });

  const failedItems = params.jobs.flatMap<FailedTeamLobbyRenderItem>((job) => {
    if (job.status !== "error" || job.acknowledgedAt !== null) {
      return [];
    }

    return [
      {
        job,
        key: `job:${job.jobId}`,
        kind: "synthetic-failed",
      },
    ];
  });

  const teamItems = params.teams.map<TeamLobbyTeamRenderItem>((team) => ({
    highlighted: highlightedTeamIds.has(team.id),
    key: `team:${team.id}`,
    kind: "team",
    team,
  }));

  const items = [
    ...(params.editMode
      ? ([
          {
            key: "create-button",
            kind: "create-button",
          },
        ] satisfies TeamLobbyRenderItem[])
      : []),
    ...activeItems,
    ...failedItems,
    ...teamItems,
  ];

  return {
    activeItems,
    failedItems,
    hasAutoOpenBlockingJobs:
      activeItems.length > 0 || unmatchedSuccessfulJobIds.size > 0,
    hasVisibleRows:
      activeItems.length > 0 || failedItems.length > 0 || teamItems.length > 0,
    highlightedTeamIds,
    items,
    shouldPollJobs:
      params.jobs.some((job) => isActiveCreateTeamStatus(job.status)) ||
      unmatchedSuccessfulJobIds.size > 0,
    teamItems,
    unmatchedSuccessfulJobIds,
  };
}
